import { UsergroupAddOutlined } from '@ant-design/icons';
import { List } from '@refinedev/antd';
import {
  IResourceComponentsProps,
  useCustom,
  useCustomMutation,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';
import {
  Alert,
  Button,
  Modal,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import {
  ClientName,
  ClientNameInText,
  NAME_TOKEN,
  ShowNamesButton,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { practiceMemberLabel, usePracticeMembers } from '../../hooks';

const { Text } = Typography;

type Person = {
  _id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

type UnassignedClientRow = {
  subject: string;
  performer?: string | null;
  practiceId: string;
  releasedAt: string;
  user?: Person | null;
  /** P022 (finding 51): the member they were released from — no longer on the roster, so resolved server-side. */
  releasedFrom?: Person | null;
  /** D-h (P022): the request waiting for the client's answer, when the practice has asked. */
  pendingTransfer?: {
    newAssignee: string;
    newAssigneeName?: string | null;
    requestedAt: string;
  } | null;
};

/**
 * E07 · UnassignedClients — THE PRACTICE POOL (M7, board cc-rtm; SDL `be/sharing.gql` §2).
 *
 * ─── WHAT THE POOL IS ──────────────────────────────────────────────────────────────────────────
 * When a practitioner leaves a practice their clients are RELEASED rather than deleted or silently
 * reattached: their entitlement is untouched, their data is untouched, and they wait here until an
 * admin gives them a new practitioner. Before M7 there was no pool and no page — a departing
 * member's clients simply stopped being anybody's, invisibly.
 *
 * ─── ASSIGN SENDS THE REQUEST (D-h, founder ruling 2026-09-19, P022) ───────────
 * Client approval is a hard precondition (founder, 2026-07-20), and until P022 this page's Assign
 * ran `reassignClient` — refused with a message written for the CLIENT whenever no consent was on
 * record, which is always, because nobody had been asked (the founder's 6b: "assigning the user to
 * myself seems an absolutely realistic scenario"). Now Assign issues `requestClientTransfer` — the
 * same request the client row sends — the client is told in the app and by email (D-g), and the
 * gears move them out of the pool the moment they approve. The row shows the waiting request.
 *
 * ─── A CLIENT WHO JOINED BY THE PRACTICE LINK (F-211, founder Q-join-1 A, 2026-09-30) ───────
 * Joining through the link connects the client to nobody, so they wait here too: no performer (nobody
 * released them), so the "Released from" cell reads "Joined through the practice link" and the time
 * is when they joined. Assign is the same request.
 *
 * ─── WHO THEY WERE RELEASED FROM (finding 51, P022) ─────────────────────────
 * The member a client is released from has just LEFT the roster this page holds, so labelling the
 * key from the roster showed `users/245100` to the founder. `UnassignedClient.releasedFrom` is
 * resolved server-side from the row itself; the roster map is only the fallback.
 */
export const UnassignedClientList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const locale = useGetLocale();
  const currentLocale = locale();
  // Q-mt-3 A: client names hidden by default, exactly like the Clients list
  const names = useClientNamesVisibility();

  const [reassigning, setReassigning] = useState<UnassignedClientRow | null>(
    null,
  );
  const [requested, setRequested] = useState(false);
  const [newAssignee, setNewAssignee] = useState<string | undefined>();
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, isFetching, refetch } = useCustom<{
    unassignedClients: { data: UnassignedClientRow[]; count: number };
  }>({
    url: '',
    method: 'get',
    meta: {
      operation: 'unassignedClients',
      fields: [
        {
          data: [
            'subject',
            'performer',
            'practiceId',
            'releasedAt',
            { user: ['_id', 'firstName', 'lastName', 'email'] },
            { releasedFrom: ['_id', 'firstName', 'lastName', 'email'] },
            {
              pendingTransfer: [
                'newAssignee',
                'newAssigneeName',
                'requestedAt',
              ],
            },
          ],
        },
        'count',
      ],
      variables: {
        start: { name: 'start', type: 'Int', value: 0 },
        limit: { name: 'limit', type: 'Int', value: 50 },
      },
    },
  });

  const rows = useMemo<UnassignedClientRow[]>(() => {
    const list = (data as any)?.data?.data ?? [];
    return Array.isArray(list) ? list.filter(Boolean) : [];
  }, [data]);

  const { members } = usePracticeMembers();
  const { mutateAsync: runReassign } = useCustomMutation();

  const memberLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    members.forEach((member) => {
      map[member._id] = practiceMemberLabel(member);
    });
    return map;
  }, [members]);

  // Q-mt-3b A: "id · initials", full name on hover; a missing name is just the id — never the e-mail
  // and never the person-reference key
  const clientLabel = useCallback(
    (row: UnassignedClientRow) => ({
      id: row.user?._id,
      firstName: row.user?.firstName,
      lastName: row.user?.lastName,
    }),
    [],
  );

  const closeModal = useCallback(() => {
    setReassigning(null);
    setNewAssignee(undefined);
    setFailure(null);
    setRequested(false);
  }, []);

  const releasedFromLabel = useCallback(
    (row: UnassignedClientRow) => {
      const person = row.releasedFrom;
      const name = [person?.firstName, person?.lastName]
        .filter(Boolean)
        .join(' ');
      if (name || person?.email) {
        return [name, person?.email].filter(Boolean).join(' · ');
      }
      // F-211 (Q-join-1 A): a client who joined through the practice link was released by nobody
      if (!row.performer) {
        return translate('portalClients.unassigned.joinedByLink');
      }
      return memberLabelById[row.performer] ?? row.performer;
    },
    [memberLabelById, translate],
  );

  const confirmReassign = useCallback(async () => {
    if (!reassigning || !newAssignee) {
      return;
    }

    setSaving(true);
    setFailure(null);

    try {
      await runReassign({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          // D-h: the SAME request the client row sends — the client approves, the gears do the rest
          operation: 'requestClientTransfer',
          fields: ['success', 'subject'],
          variables: {
            input: {
              name: 'input',
              type: 'RequestClientTransferInput!',
              value: { subject: reassigning.subject, newAssignee },
            },
          },
        },
      });

      setRequested(true);
      await refetch();
    } catch (error) {
      // The server's own coded message names which precondition failed (not a practice admin, the
      // member is in another practice, …) and is already actionable in both languages.
      setFailure(
        (error as { message?: string })?.message ||
          translate('portalClients.transfer.failed'),
      );
    } finally {
      setSaving(false);
    }
  }, [newAssignee, reassigning, refetch, runReassign, translate]);

  const formatReleasedAt = useCallback(
    (value?: string | null) => {
      if (!value) {
        return undefined;
      }

      return format(
        new Date(value),
        currentLocale?.startsWith('ru')
          ? 'yyyy-MM-dd HH:mm'
          : 'yyyy-MM-dd hh:mm aaa',
      );
    },
    [currentLocale],
  );

  return (
    <List
      title={translate('portalClients.unassigned.title')}
      headerProps={{
        extra: (
          <ShowNamesButton
            visible={names.visible}
            onToggle={names.toggle}
            testId="cc-unassigned-clients-toggle-names"
          />
        ),
      }}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={translate('portalClients.unassigned.intro')}
        description={translate('portalClients.unassigned.consentNote')}
        data-testid="cc-unassigned-clients-intro"
      />

      <Table
        dataSource={rows}
        loading={isFetching}
        rowKey="subject"
        pagination={false}
        data-testid="cc-unassigned-clients-list"
        locale={{ emptyText: translate('portalClients.unassigned.empty') }}
      >
        <Table.Column<UnassignedClientRow>
          key="client"
          title={translate('portalClients.unassigned.client')}
          render={(_, record) => (
            <Text>
              <ClientName {...clientLabel(record)} visible={names.visible} />
            </Text>
          )}
        />
        <Table.Column<UnassignedClientRow>
          key="performer"
          title={translate('portalClients.unassigned.releasedFrom')}
          render={(_, record) => (
            <Text
              type="secondary"
              data-testid="cc-unassigned-clients-released-from"
            >
              {releasedFromLabel(record)}
            </Text>
          )}
        />
        <Table.Column<UnassignedClientRow>
          key="releasedAt"
          title={translate('portalClients.unassigned.releasedAt')}
          render={(_, record) => formatReleasedAt(record.releasedAt)}
        />
        <Table.Column<UnassignedClientRow>
          key="actions"
          title={translate('resourcesCommon.actions')}
          render={(_, record) => (
            <Space direction="vertical" size={4}>
              {record.pendingTransfer ? (
                <Text
                  type="secondary"
                  data-testid="cc-unassigned-clients-pending"
                >
                  {translate('portalClients.unassigned.pending', {
                    member:
                      record.pendingTransfer.newAssigneeName ||
                      memberLabelById[record.pendingTransfer.newAssignee] ||
                      record.pendingTransfer.newAssignee,
                    when: formatReleasedAt(record.pendingTransfer.requestedAt),
                  })}
                </Text>
              ) : null}
              <Tooltip
                title={translate(
                  record.pendingTransfer
                    ? 'portalClients.unassigned.pendingTip'
                    : 'portalClients.unassigned.reassignTip',
                )}
              >
                <Button
                  size="small"
                  icon={<UsergroupAddOutlined />}
                  onClick={() => {
                    setFailure(null);
                    setNewAssignee(undefined);
                    setRequested(false);
                    setReassigning(record);
                  }}
                  data-testid="cc-unassigned-clients-reassign"
                >
                  {translate('portalClients.unassigned.reassign')}
                </Button>
              </Tooltip>
            </Space>
          )}
        />
      </Table>

      <Modal
        open={Boolean(reassigning)}
        title={translate('portalClients.unassigned.reassignTitle')}
        okText={translate(
          requested
            ? 'portalClients.unassigned.close'
            : 'portalClients.unassigned.reassign',
        )}
        cancelButtonProps={
          requested ? { style: { display: 'none' } } : undefined
        }
        cancelText={translate('buttons.cancel')}
        confirmLoading={saving}
        okButtonProps={{
          'disabled': !requested && !newAssignee,
          'data-testid': 'cc-unassigned-clients-reassign-confirm',
        }}
        onOk={() => {
          if (requested) {
            closeModal();
            return;
          }
          confirmReassign().catch(() => undefined);
        }}
        onCancel={closeModal}
      >
        {requested ? (
          <Alert
            type="success"
            showIcon
            message={
              <ClientNameInText
                text={translate('portalClients.unassigned.requested', {
                  name: NAME_TOKEN,
                  member: memberLabelById[newAssignee ?? ''] ?? '',
                })}
                {...(reassigning ? clientLabel(reassigning) : {})}
                visible={names.visible}
              />
            }
            description={translate('portalClients.unassigned.requestedNote')}
            data-testid="cc-unassigned-clients-requested"
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>
              <ClientNameInText
                text={translate('portalClients.unassigned.reassignBody', {
                  name: NAME_TOKEN,
                })}
                {...(reassigning ? clientLabel(reassigning) : {})}
                visible={names.visible}
              />
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder={translate(
                'portalClients.unassigned.choosePractitioner',
              )}
              value={newAssignee}
              onChange={setNewAssignee}
              options={members.map((member) => ({
                value: member._id,
                label: practiceMemberLabel(member),
              }))}
              data-testid="cc-unassigned-clients-assignee"
            />
            <Text type="secondary">
              {translate('portalClients.unassigned.reassignConsentNote')}
            </Text>
            {failure ? <Alert type="error" showIcon message={failure} /> : null}
          </Space>
        )}
      </Modal>
    </List>
  );
};
